import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Order, Product, Stock, SelectedOption } from '../shared/types';
import { apiService } from '../services/api';
import { formatOrderIdForDisplay, getDisplayAddress } from '../shared/orderUtils';
import { getAdminPassword } from '../shared/authConfig';
import { londonDateKey, londonWallClockToUTC } from '../shared/dateUtils';
import './KitchenView.css';

// ============================================================================
// Types
// ============================================================================

interface AggregatedStockItem {
    stockId: string;
    name: string;
    totalQuantity: number;
    sortOrder: number;
}

interface OrderItemWithLetter {
    letter: string;
    name: string;
    quantity: number;
    price: number;
    productId: string;
    selectedOptions?: SelectedOption[];
}

interface ChoiceDisplay {
    name: string;
    type: 'keep' | 'remove' | 'add' | 'unselected';
}

interface OptionDisplay {
    optionName: string;
    choices: ChoiceDisplay[];
}

// Map of productId -> optionId -> Set of visible choiceIds
type VisibleChoicesMap = Map<string, Map<string, Set<string>>>;

// ============================================================================
// Letter Generation Utility
// ============================================================================

function getItemLetter(index: number): string {
    let result = '';
    let n = index;
    do {
        result = String.fromCharCode(65 + (n % 26)) + result;
        n = Math.floor(n / 26) - 1;
    } while (n >= 0);
    return result;
}

// ============================================================================
// Stock Aggregation
// ============================================================================

function aggregateOrderStockItems(order: Order, products: Product[], stocks: Stock[]): AggregatedStockItem[] {
    const stockUsageMap = new Map<string, number>();
    const stockMap = new Map(stocks.map(s => [s.id, s]));
    const productMap = new Map(products.map(p => [p.id, p]));

    for (const item of order.items) {
        const product = productMap.get(item.productId);
        if (!product) continue;

        const itemQuantity = item.quantity || 1;

        if (Array.isArray(product.stockIds)) {
            for (const stockId of product.stockIds) {
                stockUsageMap.set(stockId, (stockUsageMap.get(stockId) ?? 0) + itemQuantity);
            }
        }

        if (Array.isArray(item.selectedOptions) && Array.isArray(product.options)) {
            const optionMap = new Map(product.options.map(opt => [opt.id, opt]));
            for (const selection of item.selectedOptions as SelectedOption[]) {
                const option = optionMap.get(selection.optionId);
                if (!option || !Array.isArray(selection.choiceIds)) continue;

                const choiceMap = new Map(option.choices.map(choice => [choice.id, choice]));
                for (const choiceId of selection.choiceIds) {
                    const choice = choiceMap.get(choiceId);
                    if (!choice || !Array.isArray(choice.stockIds)) continue;

                    for (const stockId of choice.stockIds) {
                        stockUsageMap.set(stockId, (stockUsageMap.get(stockId) ?? 0) + itemQuantity);
                    }
                }
            }
        }
    }

    const aggregatedItems: AggregatedStockItem[] = [];
    for (const [stockId, totalQuantity] of stockUsageMap) {
        if (totalQuantity <= 0) continue;
        const stock = stockMap.get(stockId);
        aggregatedItems.push({
            stockId,
            name: stock?.name || stockId,
            totalQuantity,
            sortOrder: stock?.sortOrder ?? Number.MAX_SAFE_INTEGER,
        });
    }

    aggregatedItems.sort((a, b) => {
        if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
        return a.name.localeCompare(b.name);
    });

    return aggregatedItems;
}

/**
 * Build a map of which choices should be visible for each product.
 * A choice is visible if:
 * - It is default-selected on the product definition, OR
 * - It is customer-selected on at least one order item of that product
 */
function buildVisibleChoicesMap(items: OrderItemWithLetter[], products: Product[]): VisibleChoicesMap {
    const map: VisibleChoicesMap = new Map();

    // Group items by productId
    const itemsByProduct = new Map<string, OrderItemWithLetter[]>();
    for (const item of items) {
        const existing = itemsByProduct.get(item.productId) || [];
        existing.push(item);
        itemsByProduct.set(item.productId, existing);
    }

    // For each unique product in the order
    for (const [productId, productItems] of itemsByProduct) {
        const product = products.find(p => p.id === productId);
        if (!product?.options) continue;

        const optionMap = new Map<string, Set<string>>();

        for (const option of product.options) {
            const visibleChoiceIds = new Set<string>();

            for (const choice of option.choices) {
                // Show if default-selected
                if (choice.defaultSelected) {
                    visibleChoiceIds.add(choice.id);
                    continue;
                }

                // Show if customer-selected on ANY item of this product
                for (const item of productItems) {
                    const selectedOption = item.selectedOptions?.find(so => so.optionId === option.id);
                    if (selectedOption?.choiceIds?.includes(choice.id)) {
                        visibleChoiceIds.add(choice.id);
                        break;
                    }
                }
            }

            optionMap.set(option.id, visibleChoiceIds);
        }

        map.set(productId, optionMap);
    }

    return map;
}

function getOptionDisplays(
    item: OrderItemWithLetter,
    products: Product[],
    visibleChoicesMap: VisibleChoicesMap
): OptionDisplay[] {
    const product = products.find(p => p.id === item.productId);
    if (!product?.options) return [];

    const productVisibility = visibleChoicesMap.get(item.productId);
    const optionDisplays: OptionDisplay[] = [];

    // Sort options by order
    const sortedOptions = [...product.options].sort((a, b) => {
        const aOrder = a.order ?? 999999;
        const bOrder = b.order ?? 999999;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return a.name.localeCompare(b.name);
    });

    for (const option of sortedOptions) {
        const selectedOption = item.selectedOptions?.find(so => so.optionId === option.id);
        const selectedChoiceIds = selectedOption?.choiceIds || [];
        const visibleChoiceIds = productVisibility?.get(option.id) || new Set<string>();

        // Sort choices by order
        const sortedChoices = [...option.choices].sort((a, b) => {
            const aOrder = a.order ?? 999999;
            const bOrder = b.order ?? 999999;
            if (aOrder !== bOrder) return aOrder - bOrder;
            return a.name.localeCompare(b.name);
        });

        const choices: ChoiceDisplay[] = [];
        for (const choice of sortedChoices) {
            // Skip choices that are not in the master list for this product group
            if (!visibleChoiceIds.has(choice.id)) {
                continue;
            }

            const isSelected = selectedChoiceIds.includes(choice.id);
            const isDefault = choice.defaultSelected || false;

            if (isDefault && isSelected) {
                // Default selected and kept - plain text
                choices.push({ name: choice.name, type: 'keep' });
            } else if (isDefault && !isSelected) {
                // Default selected but removed - strikethrough in box
                choices.push({ name: choice.name, type: 'remove' });
            } else if (!isDefault && isSelected) {
                // Not default but added - name in box (bold)
                choices.push({ name: choice.name, type: 'add' });
            } else {
                // Not default, not selected on THIS item, but in master list
                // (another item of same product has it) - strikethrough without box
                choices.push({ name: choice.name, type: 'unselected' });
            }
        }

        if (choices.length > 0) {
            optionDisplays.push({ optionName: option.name, choices });
        }
    }

    return optionDisplays;
}

// ============================================================================
// Helper Functions
// ============================================================================

function getTotalOrderQuantity(order: Order): number {
    return order.items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
}

function isLargeOrder(order: Order): boolean {
    return getTotalOrderQuantity(order) > 4;
}

function hasChipsInOrder(order: Order, products: Product[], stocks: Stock[]): boolean {
    const aggregatedItems = aggregateOrderStockItems(order, products, stocks);
    return aggregatedItems.some(item => {
        const stock = stocks.find(s => s.id === item.stockId);
        return stock?.isChips === true;
    });
}

function isChipsOrder(order: Order, products: Product[], stocks: Stock[]): boolean {
    return !isLargeOrder(order) && hasChipsInOrder(order, products, stocks);
}

function sortOrderItems(items: Order['items'], products: Product[]): Order['items'] {
    return [...items].sort((a, b) => {
        const productA = products.find(p => p.id === a.productId);
        const productB = products.find(p => p.id === b.productId);
        if (!productA || !productB) return 0;

        const categoryCompare = (productA.category || '').localeCompare(productB.category || '');
        if (categoryCompare !== 0) return categoryCompare;

        return (productA.sortOrder ?? 999999) - (productB.sortOrder ?? 999999);
    });
}

function getTodayDateString(): string {
    return londonDateKey();
}

function getPlannedDeliveryTimestamp(order: Order): number {
    // Stored `deliveryDate` is a UTC ISO instant. When a `scheduledSlot`
    // exists, treat it as the authoritative London wall-clock time and
    // recombine it with the London calendar day of the stored instant.
    if (order.scheduledSlot && order.deliveryDate) {
        const dateKey = londonDateKey(new Date(order.deliveryDate));
        return Date.parse(londonWallClockToUTC(dateKey, order.scheduledSlot));
    }
    return order.deliveryDate ? Date.parse(order.deliveryDate) : Date.now();
}

// ============================================================================
// Main Component
// ============================================================================

const KitchenView: React.FC = () => {
    const navigate = useNavigate();
    const [orders, setOrders] = useState<Order[]>([]);
    const [products, setProducts] = useState<Product[]>([]);
    const [stocks, setStocks] = useState<Stock[]>([]);
    const [updatingOrderId, setUpdatingOrderId] = useState<string | null>(null);

    // Auth check - redirect if not authenticated
    useEffect(() => {
        const adminPass = getAdminPassword();
        if (!adminPass) {
            navigate('/melaba/login?redirect=/melaba/kitchen');
        }
    }, [navigate]);

    const fetchData = useCallback(async () => {
        try {
            const today = getTodayDateString();
            const [ordersData, productsData, stocksData] = await Promise.all([
                apiService.getKitchenOrders(today, ['created', 'preparing']),
                apiService.getProducts(),
                apiService.getStocks()
            ]);
            setOrders(ordersData);
            setProducts(productsData);
            setStocks(stocksData);
        } catch (err) {
            console.error('Kitchen view fetch error:', err);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        const interval = setInterval(fetchData, 15000);
        return () => clearInterval(interval);
    }, [fetchData]);

    const handleStatusChange = async (orderId: string, newStatus: 'preparing' | 'ready_to_deliver') => {
        setUpdatingOrderId(orderId);
        try {
            await fetch(`/api/orders/${orderId}/status`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ status: newStatus })
            });
            await fetchData();
        } catch (err) {
            console.error('Failed to update order status:', err);
        } finally {
            setUpdatingOrderId(null);
        }
    };

    // Check auth
    const adminPass = getAdminPassword();
    if (!adminPass) {
        return null;
    }

    // Combine orders: preparing first, then ready-to-prepare, each group sorted by delivery time
    const preparingOrders = orders
        .filter(o => o.status === 'preparing')
        .sort((a, b) => getPlannedDeliveryTimestamp(a) - getPlannedDeliveryTimestamp(b));

    const readyToPrepareOrders = orders
        .filter(o => o.status === 'created')
        .sort((a, b) => getPlannedDeliveryTimestamp(a) - getPlannedDeliveryTimestamp(b));

    const allOrders = [...preparingOrders, ...readyToPrepareOrders];

    return (
        <div className="kv">
            <div className="kv-orders">
                {allOrders.length === 0 ? (
                    <div className="kv-empty">No orders</div>
                ) : (
                    allOrders.map(order => (
                        <OrderCard
                            key={order.id}
                            order={order}
                            products={products}
                            stocks={stocks}
                            onStatusChange={handleStatusChange}
                            isUpdating={updatingOrderId === order.id}
                        />
                    ))
                )}
            </div>
        </div>
    );
};

// ============================================================================
// Order Card Component
// ============================================================================

interface OrderCardProps {
    order: Order;
    products: Product[];
    stocks: Stock[];
    onStatusChange: (orderId: string, newStatus: 'preparing' | 'ready_to_deliver') => void;
    isUpdating: boolean;
}

const OrderCard: React.FC<OrderCardProps> = ({
    order,
    products,
    stocks,
    onStatusChange,
    isUpdating
}) => {
    const isPreparing = order.status === 'preparing';
    const sortedItems = sortOrderItems(order.items, products);
    const itemsWithLetters: OrderItemWithLetter[] = sortedItems.map((item, index) => ({
        ...item,
        letter: getItemLetter(index)
    }));

    // Build visible choices map for smart choice alignment within this order
    const visibleChoicesMap = buildVisibleChoicesMap(itemsWithLetters, products);

    const stockSummary = aggregateOrderStockItems(order, products, stocks);
    const isLarge = isLargeOrder(order);
    const hasChips = isChipsOrder(order, products, stocks);

    const handleAction = () => {
        if (isPreparing) {
            onStatusChange(order.id, 'ready_to_deliver');
        } else {
            onStatusChange(order.id, 'preparing');
        }
    };

    // Check if there are any alerts to show
    const hasAlerts = order.slotWasBumped || isLarge || hasChips || order.deliveryNotes;

    return (
        <div className={`kv-card ${isPreparing ? 'kv-card--preparing' : 'kv-card--ready'}`}>
            {/* Header + Alerts column */}
            <div className="kv-section kv-section--header">
                <button
                    className={`kv-action ${isPreparing ? 'kv-action--complete' : 'kv-action--start'}`}
                    onClick={handleAction}
                    disabled={isUpdating}
                >
                    {isUpdating ? '...' : isPreparing ? '✓ COMPLETE' : '▶ START'}
                </button>
                <div className="kv-customer">
                    <span className="kv-name">{order.customerName || 'No name'}</span>
                    <span className="kv-address">{getDisplayAddress(order, 'No address')}</span>
                </div>
                <div className="kv-meta">
                    <span className="kv-id">#{formatOrderIdForDisplay(order.id)}</span>
                    <span className={`kv-time ${order.deliverNow ? 'kv-time--instant' : ''}`}>
                        {order.deliverNow ? '⚡' : '📅'} {order.scheduledSlot || '--:--'}
                    </span>
                    <a
                        href={`/melaba/order-print/${order.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="kv-print-link"
                        title="Print order labels"
                    >
                        🖨️ Print
                    </a>
                </div>
                {/* Alerts inside header section */}
                {hasAlerts && (
                    <div className="kv-alerts">
                        {order.slotWasBumped && (
                            <div className="kv-alert kv-alert--bump">
                                ⚠️ Bumped{order.originalRequestedSlot ? ` from ${order.originalRequestedSlot}` : ''}
                            </div>
                        )}
                        {isLarge && (
                            <div className="kv-alert kv-alert--info">📢 Large order</div>
                        )}
                        {hasChips && (
                            <div className="kv-alert kv-alert--info">📢 Chips order</div>
                        )}
                        {order.deliveryNotes && (
                            <div className="kv-alert kv-alert--notes">📝 {order.deliveryNotes}</div>
                        )}
                    </div>
                )}
            </div>

            {/* Stock column */}
            <div className="kv-section">
                <div className="kv-section-title">Stock</div>
                {stockSummary.map(item => (
                    <div key={item.stockId} className="kv-stock-row">
                        <span className="kv-stock-qty">{item.totalQuantity}×</span>
                        <span className="kv-stock-name">{item.name}</span>
                    </div>
                ))}
            </div>

            {/* Each item as its own column */}
            {itemsWithLetters.map((item, idx) => (
                <div key={idx} className="kv-section">
                    <ItemRow
                        item={item}
                        products={products}
                        visibleChoicesMap={visibleChoicesMap}
                    />
                </div>
            ))}
        </div>
    );
};

// ============================================================================
// Item Row Component
// ============================================================================

interface ItemRowProps {
    item: OrderItemWithLetter;
    products: Product[];
    visibleChoicesMap: VisibleChoicesMap;
}

const ItemRow: React.FC<ItemRowProps> = ({ item, products, visibleChoicesMap }) => {
    const optionDisplays = getOptionDisplays(item, products, visibleChoicesMap);

    return (
        <div className="kv-item">
            <div className="kv-item-main">
                <span className="kv-item-letter">{item.letter}</span>
                <span className="kv-item-qty">{item.quantity}×</span>
                <span className="kv-item-name">{item.name}</span>
            </div>
            {optionDisplays.length > 0 && (
                <div className="kv-item-options">
                    {optionDisplays.map((group, gi) => (
                        <div key={gi} className="kv-option-group">
                            <div className="kv-option-title">{group.optionName}</div>
                            <div className="kv-option-choices">
                                {group.choices.map((c, i) => {
                                    let emoji = '';
                                    if (c.type === 'keep') emoji = '✓';
                                    else if (c.type === 'remove') emoji = '✗';
                                    else if (c.type === 'add') emoji = '➕';

                                    return (
                                        <div key={i} className="kv-choice-row">
                                            <span className={`kv-opt kv-opt--${c.type}`}>
                                                {emoji ? `${emoji} ` : ''}{c.name}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default KitchenView;
